import { useCallback, useEffect, useRef, useState } from "react";
import { F, mediaKind, type Row } from "./types";
import { fullSrc, thumbSrc } from "./rows";
import {
  clampPan, dismissProgress, fitBox, midpoint, nextScale, pinchScale, spread, type Pt,
} from "./gesture";

/** The full-screen media viewer.
 *
 *  It holds no arithmetic of its own -- every number comes from gesture.ts. That
 *  is not a style preference: jsdom has `TouchEvent` but no `Touch`, so a
 *  realistic two-finger event cannot be built in a test, and anything computed
 *  in here would be untestable by construction. What this file is responsible
 *  for is reading touches, applying a transform, and knowing what closes it.
 *
 *  It renders as a sibling of the scroller, never inside it: a child of a
 *  scrolling, overflow-hidden container inherits both, and the viewer would be
 *  clipped and would scroll away with the list underneath it.
 */
export function Viewer({ rows, order, at, onAt, onClose, vw, vh }: {
  rows: Row[];
  /** Row indices that can be opened, in timeline order. */
  order: number[];
  at: number;
  onAt: (next: number) => void;
  onClose: () => void;
  vw: number;
  vh: number;
}) {
  const row = rows[order[at]];
  const isVideo = mediaKind(row) === "video";
  const viewport = { width: vw, height: vh };
  const box = fitBox(row[F.W], row[F.H], viewport);

  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState<Pt>({ x: 0, y: 0 });
  const [dragY, setDragY] = useState(0);
  const grab = useRef<{ spread: number; scale: number; from: Pt; pan: Pt } | null>(null);
  const lastTap = useRef(0);

  const first = at === 0;
  const last = at === order.length - 1;

  // A new photo starts fitted. Carrying a 3x zoom across from the last one
  // means the next picture opens somewhere in its own top-left corner.
  useEffect(() => { setScale(1); setPan({ x: 0, y: 0 }); setDragY(0); }, [at]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight" && !last) onAt(at + 1);
      else if (e.key === "ArrowLeft" && !first) onAt(at - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [at, first, last, onAt, onClose]);

  const point = (t: { clientX: number; clientY: number }): Pt => ({ x: t.clientX, y: t.clientY });

  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches;
    grab.current = t.length >= 2
      ? { spread: spread(point(t[0]), point(t[1])), scale, from: midpoint(point(t[0]), point(t[1])), pan }
      : { spread: 0, scale, from: point(t[0]), pan };

    if (t.length === 1) {
      const now = Date.now();
      if (now - lastTap.current < 300) { toggleZoom(); lastTap.current = 0; }
      else lastTap.current = now;
    }
  };

  const onTouchMove = (e: React.TouchEvent) => {
    const g = grab.current;
    if (!g) return;
    const t = e.touches;

    if (t.length >= 2) {
      const next = pinchScale(g.scale, g.spread, spread(point(t[0]), point(t[1])));
      setScale(next);
      setPan(clampPan(g.pan, next, box, viewport));
      return;
    }

    const p = point(t[0]);
    if (scale > 1) {
      // Zoomed in, a drag pans. clampPan is what stops you dragging the photo
      // off the screen entirely and being left looking at black.
      setPan(clampPan(
        { x: g.pan.x + (p.x - g.from.x), y: g.pan.y + (p.y - g.from.y) },
        scale, box, viewport,
      ));
    } else {
      // Fitted, a drag dismisses -- either direction, because a photo near the
      // top of the screen is as natural to flick up as down.
      setDragY(p.y - g.from.y);
    }
  };

  const onTouchEnd = () => {
    grab.current = null;
    if (dismissProgress(dragY) >= 1) onClose();
    else setDragY(0);
  };

  const toggleZoom = useCallback(() => {
    const next = nextScale(scale);
    setScale(next);
    setPan((p) => clampPan(p, next, box, viewport));
  }, [scale, box, viewport.width, viewport.height]); // eslint-disable-line react-hooks/exhaustive-deps

  // Only the ones you could reach with the very next gesture. Preloading the
  // whole archive would be 5,334 originals for one tap.
  const neighbours = [at - 1, at + 1]
    .filter((i) => i >= 0 && i < order.length)
    .map((i) => fullSrc(rows[order[i]])!);

  const fade = 1 - dismissProgress(dragY) * 0.7;

  return (
    <div
      className="viewer"
      data-testid="viewer"
      role="dialog"
      aria-modal="true"
      aria-label="media viewer"
      style={{ backgroundColor: `rgba(0, 0, 0, ${fade.toFixed(3)})` }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
    >
      <div
        className="vstage"
        style={{ transform: `translate(${pan.x}px, ${pan.y + dragY}px) scale(${scale})` }}
      >
        {isVideo ? (
          // playsInline still matters even here. Without it iOS replaces this
          // viewer with its own player the instant the video starts, and every
          // gesture above belongs to Safari rather than to the archive.
          <video
            data-testid="player"
            src={fullSrc(row)!}
            poster={thumbSrc(row) ?? undefined}
            controls
            autoPlay
            playsInline
          />
        ) : (
          <img
            data-testid="player"
            src={fullSrc(row)!}
            alt="full size"
            decoding="async"
            style={{ width: box.width, height: box.height }}
            onDoubleClick={toggleZoom}
          />
        )}
      </div>

      <div className="vbar">
        <button type="button" aria-label="previous photo" disabled={first}
                onClick={() => onAt(at - 1)}>&#8249;</button>
        <span className="vcount">{at + 1} of {order.length}</span>
        <button type="button" aria-label="next photo" disabled={last}
                onClick={() => onAt(at + 1)}>&#8250;</button>
      </div>

      <button type="button" className="vclose" aria-label="close viewer" onClick={onClose}>
        &#10005;
      </button>

      {/* Hidden <img> rather than a fetch: the browser decodes it too, so the
          neighbour is ready to paint and not merely downloaded. */}
      {neighbours.map((src) => (
        <img key={src} className="preload" src={src} alt="" aria-hidden="true" />
      ))}
    </div>
  );
}
